// Field boxes returned by GET Documents/field-positions (server: OcrFieldPositions).
//
// Payload (version 2): { version, coordinateSpace: 'normalized_1000', documentKey, pageCount,
//   fields: { entityField: [{ page, box: [ymin, xmin, ymax, xmax] }] }, values: { entityField: canonical } }
// Boxes are on a 0-1000 scale relative to the page as displayed (origin top-left), so they map to
// CSS percentages of the rendered page whatever its size.

export const SUPPORTED_VERSION = 2;
export const COORDINATE_SPACE = 'normalized_1000';

const SCALE = 1000;
const MAX_PAGES = 500;
const MAX_BOXES_PER_FIELD = 10;

const pick = (obj, camel) => {
    if (!obj || typeof obj !== 'object') return undefined;
    if (camel in obj) return obj[camel];
    const pascal = camel.charAt(0).toUpperCase() + camel.slice(1);
    return obj[pascal];
};

const isValidBox = (page, box, pageCount) =>
    Number.isInteger(page) && page >= 1 && page <= pageCount
    && Array.isArray(box) && box.length === 4
    && box.every((c) => Number.isFinite(c) && c >= 0 && c <= SCALE)
    && box[0] < box[2] && box[1] < box[3];

/**
 * Validates the server payload. Returns null when nothing usable is left, otherwise
 * { documentKey, pageCount, fields: { name: [{ page, top, left, width, height }] }, values }
 * with geometry in percent of the page.
 */
export function normalizeFieldPositions(payload) {
    if (!payload || typeof payload !== 'object') return null;
    if (pick(payload, 'version') !== SUPPORTED_VERSION) return null;
    if (pick(payload, 'coordinateSpace') !== COORDINATE_SPACE) return null;

    const pageCount = pick(payload, 'pageCount');
    if (!Number.isInteger(pageCount) || pageCount < 1 || pageCount > MAX_PAGES) return null;

    const rawFields = pick(payload, 'fields');
    if (!rawFields || typeof rawFields !== 'object') return null;
    const rawValues = pick(payload, 'values') || {};

    const fields = {};
    const values = {};
    Object.entries(rawFields).forEach(([name, boxes]) => {
        if (!Array.isArray(boxes)) return;
        const valid = boxes
            .map((b) => ({ page: pick(b, 'page'), box: pick(b, 'box') }))
            .filter((b) => isValidBox(b.page, b.box, pageCount))
            .slice(0, MAX_BOXES_PER_FIELD)
            .map(({ page, box }) => ({ page, ...boxToPercent(box) }));
        if (valid.length === 0) return;
        fields[name] = valid;
        if (typeof rawValues[name] === 'string') values[name] = rawValues[name];
    });

    if (Object.keys(fields).length === 0) return null;
    return {
        documentKey: pick(payload, 'documentKey') || null,
        pageCount,
        fields,
        values,
    };
}

/** [ymin, xmin, ymax, xmax] (0-1000) → CSS percentages. */
export function boxToPercent([ymin, xmin, ymax, xmax]) {
    return {
        top: ymin / 10,
        left: xmin / 10,
        width: (xmax - xmin) / 10,
        height: (ymax - ymin) / 10,
    };
}

/** Boxes on one page: [{ field, top, left, width, height }]. */
export function boxesOnPage(positions, page, visibleFields = null) {
    if (!positions) return [];
    const result = [];
    Object.entries(positions.fields).forEach(([field, boxes]) => {
        if (visibleFields && !visibleFields.has(field)) return;
        boxes.forEach((b, index) => {
            if (b.page === page) result.push({ field, index, top: b.top, left: b.left, width: b.width, height: b.height });
        });
    });
    return result;
}

const pathOf = (urlOrKey) => {
    if (!urlOrKey || typeof urlOrKey !== 'string') return '';
    let path = urlOrKey.trim();
    if (/^https?:\/\//i.test(path)) {
        try {
            path = new URL(path).pathname;
        } catch {
            return '';
        }
    } else {
        path = path.split(/[?#]/)[0];
    }
    try {
        path = decodeURIComponent(path);
    } catch {
        // keep the raw path
    }
    return path.replace(/^\/+/, '');
};

/**
 * True when the displayed document (presigned URL or S3 key) is the one the boxes were drawn on.
 * Without a key on the payload the server has already checked the entity's document.
 */
export function isSameDocument(displayedUrl, documentKey) {
    if (!documentKey) return true;
    const shown = pathOf(displayedUrl);
    const key = pathOf(documentKey);
    if (!shown || !key) return false;
    return shown === key || shown.endsWith(`/${key}`);
}

// ── Canonical values (mirror of the server's OcrFieldPositions.Canonical) ────────────────────────

const LETTER_OR_DIGIT = /[\p{L}\p{Nd}]/u;
const DIGIT = /\p{Nd}/u;

export const canonical = {
    text: (value) => (value == null ? '' : Array.from(String(value)).filter((c) => LETTER_OR_DIGIT.test(c)).join('').toLowerCase()),
    digits: (value) => (value == null ? '' : Array.from(String(value)).filter((c) => DIGIT.test(c)).join('')),
    number: (value) => {
        if (value == null || value === '') return '';
        const n = typeof value === 'number' ? value : Number(String(value).replace(/[,\s₪$€]/g, ''));
        if (!Number.isFinite(n) || n === 0) return '';
        return String(Math.round(n * 10000) / 10000);
    },
    date: (value) => {
        if (!value) return '';
        if (typeof value === 'string') {
            const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
            if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
            const dmy = value.match(/^(\d{1,2})[/.](\d{1,2})[/.](\d{4})/);
            if (dmy) return `${dmy[3]}-${dmy[2].padStart(2, '0')}-${dmy[1].padStart(2, '0')}`;
        }
        const d = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(d.getTime())) return '';
        const mm = String(d.getMonth() + 1).padStart(2, '0');
        const dd = String(d.getDate()).padStart(2, '0');
        return `${d.getFullYear()}-${mm}-${dd}`;
    },
};

/**
 * Fields whose box still describes what the form shows: the current value (canonicalized the
 * same way the server stored it) equals the stored value. Fields absent from currentValues are
 * kept as long as the server returned them.
 */
export function visibleFieldSet(positions, currentValues = {}) {
    const visible = new Set();
    if (!positions) return visible;
    Object.keys(positions.fields).forEach((field) => {
        const stored = positions.values[field];
        if (!(field in currentValues) || stored === undefined || currentValues[field] === stored) {
            visible.add(field);
        }
    });
    return visible;
}

const PALETTE = ['#304FFF', '#00A884', '#E8590C', '#AE3EC9', '#1C7ED6', '#D6336C', '#2F9E44', '#F08C00'];

/** Stable color per field name. */
export function colorForField(field, fieldOrder = []) {
    const index = fieldOrder.indexOf(field);
    if (index >= 0) return PALETTE[index % PALETTE.length];
    let hash = 0;
    for (let i = 0; i < field.length; i += 1) hash = (hash * 31 + field.charCodeAt(i)) >>> 0;
    return PALETTE[hash % PALETTE.length];
}
